/**
 * Sending a DM: NIP-17, which is what every other Nostr client reads.
 *
 * One rumor, wrapped once per recipient and once for this account, so the
 * conversation is on every device that holds the key. Each wrap costs one
 * encrypt and one signature on the account's signer — the seal must be signed
 * by the account — and the wrap around it a throwaway key made here.
 *
 * Each recipient's copy goes to the relays on their kind 10050 list. With no
 * list there is nowhere they read DMs, and NIP-17 says not to guess.
 */
import {
  SimplePool, finalizeEvent, generateSecretKey, getEventHash, nip44,
  type Event as NostrEvent,
} from "nostr-tools";
import type { MailSigner } from "./signer";
import type { RelayConnection } from "./relay";
import { KIND_CHAT } from "./dm";
import { KIND_DM_RELAYS } from "./dmrelays";
import { KIND_SEAL, KIND_GIFT_WRAP } from "./outbox";
import { PROFILE_RELAYS } from "./profile";

export interface DmRequest {
  recipients: string[];
  text: string;
  subject?: string;
  inReplyTo?: string;
}

/** As the outbox does: inside every reader's window, including postrd's. */
function backdated(): number {
  return Math.floor(Date.now() / 1000) - Math.floor(Math.random() * 6 * 60 * 60);
}

/** The unsigned chat message every copy carries. */
export function chatRumor(request: DmRequest, author: string, now = Math.floor(Date.now() / 1000)): NostrEvent {
  const tags = request.recipients.map((pk) => ["p", pk]);
  if (request.inReplyTo) tags.push(["e", request.inReplyTo]);
  if (request.subject) tags.push(["subject", request.subject]);
  const rumor = { kind: KIND_CHAT, pubkey: author, created_at: now, tags, content: request.text };
  return { ...rumor, id: getEventHash(rumor), sig: "" };
}

export async function wrapFor(rumor: NostrEvent, signer: MailSigner, recipient: string): Promise<NostrEvent> {
  const seal = await signer.signEvent({
    kind: KIND_SEAL,
    created_at: backdated(),
    tags: [],
    content: await signer.nip44Encrypt(recipient, JSON.stringify(rumor)),
  });
  const nonceKey = generateSecretKey();
  const conversation = nip44.v2.utils.getConversationKey(nonceKey, recipient);
  return finalizeEvent(
    {
      kind: KIND_GIFT_WRAP,
      created_at: backdated(),
      tags: [["p", recipient]],
      content: nip44.v2.encrypt(JSON.stringify(seal), conversation),
    },
    nonceKey,
  );
}

/** Each recipient's DM relays. A recipient missing from the map has none. */
export async function dmRelaysOf(
  pubkeys: string[],
  relays: string[] = PROFILE_RELAYS,
  maxWait = 4000,
): Promise<Map<string, string[]>> {
  const pool = new SimplePool();
  try {
    const events = await pool.querySync(relays, { kinds: [KIND_DM_RELAYS], authors: pubkeys }, { maxWait });
    const newest = new Map<string, NostrEvent>();
    for (const event of events) {
      const held = newest.get(event.pubkey);
      if (!held || held.created_at < event.created_at) newest.set(event.pubkey, event);
    }
    const out = new Map<string, string[]>();
    for (const [pubkey, event] of newest) {
      const urls = event.tags.filter((t) => t[0] === "relay" && t[1]).map((t) => t[1]!);
      if (urls.length > 0) out.set(pubkey, urls);
    }
    return out;
  } finally {
    pool.close(relays);
  }
}

/**
 * Send one DM.
 *
 * Every recipient's relays are found before anything is signed, so a recipient
 * nobody can reach costs no approvals. The account's own copy goes to the mail
 * relay, which is where this client reads.
 */
export interface DmSendResult {
  delivery: DmDeliveryState;
  complete: boolean;
  failedRecipients: string[];
  selfCopySent: boolean;
}

export interface DmDeliveryState {
  rumor: NostrEvent;
  relays: Record<string, string[]>;
  wraps: Record<string, NostrEvent>;
  deliveredRecipients: string[];
  selfWrap?: NostrEvent;
  selfCopySent: boolean;
}

export async function sendDm(
  relay: RelayConnection,
  request: DmRequest,
  signer: MailSigner,
  delivery?: DmDeliveryState,
  saveDelivery: (delivery: DmDeliveryState) => Promise<void> = async () => {},
): Promise<DmSendResult> {
  let routes: Record<string, string[]>;
  if (delivery) {
    routes = delivery.relays;
  } else {
    const where = await dmRelaysOf(request.recipients);
    const unreachable = request.recipients.filter((pk) => !where.has(pk));
    if (unreachable.length > 0) {
      throw new Error(`${unreachable.length === 1 ? "this recipient has" : "some recipients have"} no DM relays listed`);
    }
    routes = Object.fromEntries(where);
    delivery = {
      rumor: chatRumor(request, signer.pubkey),
      relays: routes,
      wraps: {},
      deliveredRecipients: [],
      selfCopySent: false,
    };
  }

  await saveDelivery(delivery);
  const pool = new SimplePool();
  const delivered = new Set(delivery.deliveredRecipients);
  try {
    for (const recipient of request.recipients) {
      if (delivered.has(recipient)) continue;
      let wrap = delivery.wraps[recipient];
      if (!wrap) {
        try {
          wrap = await wrapFor(delivery.rumor, signer, recipient);
        } catch {
          continue;
        }
        delivery.wraps[recipient] = wrap;
        await saveDelivery(delivery);
      }
      try {
        await Promise.any(pool.publish(routes[recipient]!, wrap));
      } catch {
        continue;
      }
      delivered.add(recipient);
      delivery.deliveredRecipients = [...delivered];
      await saveDelivery(delivery);
    }
  } finally {
    pool.close([...new Set(Object.values(routes).flat())]);
  }

  if (!delivery.selfCopySent) {
    if (!delivery.selfWrap) {
      try {
        delivery.selfWrap = await wrapFor(delivery.rumor, signer, signer.pubkey);
      } catch {
        // Keep confirmed recipient deliveries if signing the account copy fails.
      }
    }
    if (delivery.selfWrap) {
      await saveDelivery(delivery);
      let published = false;
      try {
        await relay.publish(delivery.selfWrap);
        published = true;
      } catch {
        // The recipients may have received the message even if this copy failed.
      }
      if (published) {
        delivery.selfCopySent = true;
        await saveDelivery(delivery);
      }
    }
  }

  const failedRecipients = request.recipients.filter((pk) => !delivered.has(pk));
  return {
    delivery,
    complete: failedRecipients.length === 0 && delivery.selfCopySent,
    failedRecipients,
    selfCopySent: delivery.selfCopySent,
  };
}
